'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';

// ─── EMD Program data ────────────────────────────────────────────────────────

const programSteps = [
{
  number: '01',
  title: 'Contact Earnest Bridge',
  duration: 'Once LOI is signed',
  description:
  'Reach out to EarnestBridge once you have a signed Letter of Intent. This is the starting point — no application needed until you have an LOI in hand.',
  details: [
  { text: 'Signed Letter of Intent required' },
  { text: 'Property address and asset type' },
  { text: 'Purchase price and required EMD amount' },
  { text: 'Target dates for signing PSA and funding EMD' }],

  imageSrc: '/assets/images/emd-step1-loi-fintech.png',
  imageAlt:
  'EarnestBridge fintech dashboard showing Letter of Intent signing process with digital document and property details',
  imageLeft: true
},
{
  number: '02',
  title: 'Complete Application & EMD Approval',
  duration: 'During PSA Negotiation',
  description:
  'Complete our EMD Program application and receive approval while you negotiate the Purchase and Sale Agreement. We work in parallel with your deal timeline so nothing slows you down.',
  details: [
  { text: 'Submit EMD Program application', href: '/contact' },
  { text: 'Approval issued during PSA negotiation' },
  { text: 'No personal credit check required' },
  { text: 'Execute EMD Program agreements' }],

  imageSrc: '/assets/images/emd-step2-approval-fintech.png',
  imageAlt:
  'EarnestBridge fintech approval interface showing EMD application approved status with no credit check badge',
  imageLeft: false
},
{
  number: '03',
  title: 'We Fund Your EMD',
  duration: 'Once PSA is Signed',
  description:
  'Once the Purchase and Sale Agreement is signed, we wire your EMD directly to an approved title company. Your capital stays free while your deal is locked in.',
  details: [
  { text: 'PSA must be fully executed' },
  { text: 'Wire sent to approved title companies only' },
  { text: 'Wire confirmation provided' },
  { text: 'Deal secured — your capital preserved' }],

  imageSrc: '/assets/images/emd-step3-wire-fintech.png',
  imageAlt:
  'EarnestBridge fintech wire transfer visualization showing EMD funds sent to title company with confirmation',
  imageLeft: true
},
{
  number: '04',
  title: 'Exercise Your EMD Option',
  duration: 'Prior to Due Diligence Expiration',
  description:
  'You must exercise the EMD Option before the Option Exercise Deadline stated in your EarnestBridge agreement. EarnestBridge only funds soft deposits and maintains the right to terminate the Purchase and Sale Agreement before the EMD goes hard.',
  details: [
  { text: 'Exercise option before the Option Exercise Deadline' },
  { text: 'We only fund soft deposits and exit before EMD is hard' },
  { text: 'The option must be exercised, or an approved extension requested, before the EMD becomes non-refundable' },
  { text: 'If not exercised, the PSA terminates and EMD returns to Earnest Bridge.' },
  { text: 'Day-for-day option extensions available with approved due diligence extensions.' }],

  imageSrc: '/assets/images/emd-step4-option-fintech.png',
  imageAlt:
  'EarnestBridge fintech option exercise interface showing countdown timer and LLC transfer with due diligence deadline',
  imageLeft: false
}];


const feeItems = [
{
  icon: '💼',
  title: 'Deal Fee',
  description:
  "Calculated based on the EMD amount, due-diligence timeline, and industry. This fee is paid upfront upon acceptance into the program and covers EarnestBridge\'s cost of capital for the deposit."
},
{
  icon: '📋',
  title: 'Administrative Fee',
  description:
  'A flat $2,500 fee for document preparation and administrative processing. This fee is paid upfront alongside the Deal Fee.'
},
{
  icon: '🔄',
  title: 'Extension Fee',
  description:
  'If additional due-diligence time is needed, an option-period extension may be requested before the Option Exercise Deadline in conjunction with an approved due-diligence extension. An additional Deal Fee is charged on a prorated basis for the extension period.'
}];


const keyTerms = [
{
  icon: '🏢',
  title: 'LLC as Buyer',
  body: "EarnestBridge forms a new LLC and places the Purchase and Sale Agreement under the LLC\'s name as the buyer of record."
},
{
  icon: '🔑',
  title: 'Option to Purchase LLC',
  body: 'You receive an option to purchase the LLC at a price equal to the EMD on deposit. The option must be exercised before the Option Exercise Deadline stated in your EarnestBridge agreement.'
},
{
  icon: '📅',
  title: 'Option Exercise Deadline',
  body: 'The option must be exercised, or an approved extension requested, before the Option Exercise Deadline and before the EMD becomes non-refundable under the Purchase and Sale Agreement.'
},
{
  icon: '🛡️',
  title: 'Soft Deposits Only',
  body: "EarnestBridge only finances soft deposits. We will not extend the option period once the deposit goes hard under the Purchase and Sale Agreement."
},
{
  icon: '🏦',
  title: 'Approved Title Companies',
  body: "EMD funds are wired directly to an approved title company. EarnestBridge maintains a list of approved title companies for program eligibility."
},
{
  icon: '⚖️',
  title: 'Right to Terminate',
  body: "EarnestBridge maintains the right to terminate the Purchase and Sale Agreement upon expiration of the option period, which occurs before the EMD goes hard under the PSA."
}];


const faqs = [
{
  question: 'What is EarnestBridge and who is it best for?',
  answer: 'EarnestBridge is an earnest money deposit (EMD) financing platform for deal-makers who need to lock up deals without tying up their own capital. It is best for commercial real estate investors, syndicators, oil & gas operators, and buyers who regularly submit earnest money deposits and want to preserve liquidity across multiple simultaneous deals.'
},
{
  question: 'What types of commercial real estate deals does EarnestBridge fund?',
  answer: 'We fund EMDs for multifamily, office, industrial, retail, mixed-use, and hospitality deals across the US.'
},
{
  question: 'Do I need a personal guarantee?',
  answer: 'No. EarnestBridge is not a lender and the EMD Program is not a debt product. There is no personal guarantee and no equity surrendered. We evaluate the deal structure, property, and market — your personal assets are not at risk.'
},
{
  question: 'Do I have to pay back the EMD?',
  answer: 'EarnestBridge is not a lender and this is not a debt product. There is no personal obligation to repay the EMD unless you choose to exercise the option agreement to purchase the EarnestBridge-formed LLC. If you do not exercise the option, the PSA terminates and the EMD returns to EarnestBridge.'
},
{
  question: 'How is the fee structured?',
  answer: 'We charge two fees: (1) a flat Administrative Fee of $2,500 and (2) a Deal Fee based on the EMD amount, due-diligence timeline, and industry. Fees are disclosed upfront in the funding agreement with no hidden charges.'
},
{
  question: 'What if I need the EMD funded in less than 48 hours?',
  answer: 'Expedited funding is available in select cases. Contact us directly via email at deals@earnestbridge.com to discuss rush requests.'
},
{
  question: 'Can I use EarnestBridge for multiple deals simultaneously?',
  answer: "Yes — that\'s one of our core use cases. Many of our clients run multiple concurrent acquisitions and use EarnestBridge to deploy EMDs across all of them without tying up liquidity."
},
{
  question: 'Which states does EarnestBridge cover?',
  answer: 'EarnestBridge operates in all 50 states and the District of Columbia. Every transaction remains subject to underwriting and program eligibility. If you have questions about your specific deal, contact us at deals@earnestbridge.com.'
},
{
  question: 'Why should I choose EarnestBridge over alternatives?',
  answer: 'EarnestBridge is purpose-built for EMD financing — not a lender, not an equity partner. We operate as a silent funding partner, require no personal guarantee, no credit check, and no long-term commitment. Our 1-page application and 48–72 hour funding timeline means you never miss a deal window. Unlike self-funding or relying on equity investors, EarnestBridge lets you scale your deal pipeline without diluting your capital or giving up leverage.'
}];


// ─── Component ───────────────────────────────────────────────────────────────

export default function EMDProgramPage() {
  const heroRef = useRef<HTMLElement>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const els = heroRef.current?.querySelectorAll('[data-hero]');
    els?.forEach((el, i) => {
      const element = el as HTMLElement;
      element.style.animation = `animationIn 1s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s forwards`;
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
            el.style.filter = 'blur(0)';
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionsRef.current?.querySelectorAll('[data-reveal]');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLElement;
            el.style.opacity = '1';
            el.style.transform = 'translateY(0)';
            el.style.filter = 'blur(0)';
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = stepsRef.current?.querySelectorAll('[data-step]');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main id="main-content" tabIndex={-1} className="relative overflow-x-hidden bg-background focus-light">
      {/* Grid overlay */}
      <div className="grid-overlay">
        <div className="grid-inner">
          {[...Array(5)].map((_, i) =>
          <div key={i} className="grid-line-v" />
          )}
        </div>
      </div>

      <Header />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative pt-36 pb-24 px-6 overflow-hidden"
        style={{ background: 'var(--color-bg)' }}
        aria-label="EMD Program Hero">

        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none opacity-15"
          style={{ background: 'radial-gradient(circle, #52B788, transparent)' }} />

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-8 items-center">
            {/* Left */}
            <div className="col-span-12 lg:col-span-6">
              <div data-hero className="opacity-0">
                <p className="section-label mb-4">EMD Program</p>
              </div>
              <h1
                data-hero
                className="font-display mb-6 opacity-0"
                style={{
                  fontSize: 'clamp(2.5rem, 5.5vw, 4.75rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.025em',
                  lineHeight: 1.0
                }}>

                Earnest Bridge
                <br />
                <span className="italic" style={{ color: 'var(--color-primary)' }}>
                  EMD Program.
                </span>
                <br />
                <span style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}>Earnest Money Deposit Solutions

                </span>
              </h1>
              <p
                data-hero
                className="text-lg leading-relaxed mb-8 max-w-lg opacity-0"
                style={{ color: 'var(--color-muted)' }}>EarnestBridge forms an LLC, places your Purchase and Sale Agreement under the LLC as buyer, and funds your earnest money deposit to an approved title company — granting you an option to purchase the LLC before your deposit goes hard.</p>
              <div data-hero className="flex flex-wrap gap-4 opacity-0">
                <Link
                  href="/contact"
                  className="btn-primary text-sm font-bold uppercase tracking-[0.15em] px-8 py-4 rounded-[6px]">APPLY FOR EMD PROGRAM


                </Link>
              </div>
            </div>

            {/* Right: image + floating badge */}
            <div className="col-span-12 lg:col-span-6 relative">
              <div
                data-hero
                className="relative image-zoom rounded-3xl overflow-hidden shadow-dark opacity-0">

                <div
                  className="absolute inset-0 z-10"
                  style={{
                    background:
                    'linear-gradient(to top, rgba(13,27,23,0.55) 20%, transparent 70%)'
                  }} />

                <AppImage
                  src="/assets/images/emd-hero-fintech.png?v=2"
                  alt="EarnestBridge fintech platform dashboard showing EMD financing metrics, deal status, and commercial real estate funding data"
                  width={700}
                  height={420}
                  className="w-full object-cover"
                  style={{ aspectRatio: '16/9' }}
                  priority />

              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-4 lg:-left-8 animate-float-reverse z-20">
                <div
                  className="rounded-2xl p-4 shadow-card-hover"
                  style={{
                    background: 'white',
                    border: '1px solid rgba(27,67,50,0.1)',
                    minWidth: '160px'
                  }}>

                  <p
                    className="font-display font-bold text-2xl leading-none mb-1"
                    style={{ color: 'var(--color-primary)' }}>

                    4 Steps
                  </p>
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-muted)' }}>
                    From inquiry to funded
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div ref={sectionsRef}>
        {/* ── Program Overview Banner ───────────────────────────────────── */}
        <section
          className="py-16 px-6"
          style={{ background: 'var(--color-primary)' }}
          aria-label="Program overview">

          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              {[
              { value: 'No Debt Requirement', label: 'No personal guaranty or repayment obligation' },
              { value: 'Soft Deposits', label: 'Only during due diligence period' },
              { value: 'Option to Buy', label: 'Exercise before EMD goes hard' }].
              map((stat, i) =>
              <div
                key={i}
                data-reveal
                style={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  filter: 'blur(4px)',
                  transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`
                }}>

                  <p className="font-display font-semibold text-2xl mb-2" style={{ color: 'white' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {stat.label}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Step-by-Step Process ──────────────────────────────────────── */}
        <section
          ref={stepsRef}
          className="py-24 px-6 relative z-10"
          style={{ background: 'var(--color-surface)' }}
          aria-labelledby="process-heading">

          <div className="max-w-7xl mx-auto">
            <div
              data-step
              className="text-center mb-20"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>

              <p className="section-label mb-3">Step by Step</p>
              <h2
                id="process-heading"
                className="font-display"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05
                }}>

                From inquiry to funded EMD —
                <br />
                <span className="italic" style={{ color: 'var(--color-primary)' }}>
                  here&apos;s exactly what happens.
                </span>
              </h2>
            </div>

            <div className="space-y-28">
              {programSteps.map((step, i) =>
              <div
                key={step.number}
                data-step
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                step.imageLeft ? '' : 'lg:[&>*:first-child]:order-2'}`
                }
                style={{
                  opacity: 0,
                  transform: 'translateY(32px)',
                  filter: 'blur(6px)',
                  transition: `all 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`
                }}>

                  {/* Image */}
                  <div className="relative image-zoom rounded-3xl overflow-hidden shadow-card">
                    <div
                    className="absolute inset-0 z-10 pointer-events-none"
                    style={{
                      background:
                      'linear-gradient(to top, rgba(13,27,23,0.45) 15%, transparent 60%)'
                    }} />

                    <AppImage
                    src={step.imageSrc}
                    alt={step.imageAlt}
                    width={700}
                    height={440}
                    className="w-full object-cover"
                    style={{ aspectRatio: '16/10' }} />

                    <div className="absolute top-6 left-6 z-20">
                      <span
                      className="font-display font-bold text-5xl leading-none"
                      style={{ color: 'rgba(255,255,255,0.2)' }}>

                        {step.number}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span
                      className="font-display font-bold text-6xl leading-none"
                      style={{ color: 'rgba(27,67,50,0.08)' }}
                      aria-hidden="true">

                        {step.number}
                      </span>
                      <span
                      className="pill-badge"
                      style={{
                        background: 'rgba(82,183,136,0.12)',
                        color: 'var(--color-primary)'
                      }}>
                        <span aria-hidden="true">⏱</span> {step.duration}
                      </span>
                    </div>
                    <h3
                    className="font-display font-semibold mb-4"
                    style={{
                      fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                      color: 'var(--color-foreground)',
                      lineHeight: 1.1
                    }}>

                      {step.title}
                    </h3>
                    <p
                    className="text-base leading-relaxed mb-8"
                    style={{ color: 'var(--color-muted)' }}>

                      {step.description}
                    </p>
                    <ul className="space-y-3">
                      {step.details.map((detail) =>
                    <li key={detail.text} className="flex items-center gap-3">
                          <span
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs"
                        style={{
                          background: 'rgba(82,183,136,0.15)',
                          color: 'var(--color-primary)'
                        }}>

                            ✓
                          </span>
                          {detail.href ?
                      <Link
                        href={detail.href}
                        className="text-sm font-medium underline underline-offset-2 hover:opacity-75 transition-opacity"
                        style={{ color: 'var(--color-primary)' }}>

                              {detail.text}
                            </Link> :

                      <span
                        className="text-sm font-medium"
                        style={{ color: 'var(--color-foreground)' }}>

                              {detail.text}
                            </span>
                      }
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Fee Structure ─────────────────────────────────────────────── */}
        <section
          className="py-24 px-6"
          style={{ background: 'var(--color-bg-alt)' }}
          aria-labelledby="fees-heading">

          <div className="max-w-7xl mx-auto">
            <div
              data-reveal
              className="text-center mb-16"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>

              <p className="section-label mb-3">Fee Structure</p>
              <h2
                id="fees-heading"
                className="font-display"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05
                }}>

                Transparent fees —
                <br />
                <span className="italic" style={{ color: 'var(--color-primary)' }}>
                  no surprises.
                </span>
              </h2>
              <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: 'var(--color-muted)' }}>
                All fees are disclosed upfront before you enter the program. Two fees are required at
                acceptance; extension fees apply only if you extend the due diligence period.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {feeItems.map((fee, i) =>
              <div
                key={fee.title}
                data-reveal
                className="card-hover rounded-2xl p-8"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid rgba(27,67,50,0.1)',
                  opacity: 0,
                  transform: 'translateY(24px)',
                  filter: 'blur(4px)',
                  transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`
                }}>

                  <span className="text-3xl mb-4 block" aria-hidden="true">{fee.icon}</span>
                  <h3
                  className="font-display font-semibold text-lg mb-3"
                  style={{ color: 'var(--color-foreground)' }}>

                    {fee.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                    {fee.description}
                  </p>
                </div>
              )}
            </div>

            {/* Fee callout box */}
            <div
              data-reveal
              className="mt-10 rounded-2xl p-8 flex flex-col md:flex-row md:items-center gap-6"
              style={{
                background: 'rgba(27,67,50,0.05)',
                border: '1px solid rgba(27,67,50,0.15)',
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.3s'
              }}>

              <div
                className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ background: 'rgba(255,255,255,0.1)' }}
                aria-hidden="true">

                ℹ️
              </div>
              <div>
                <p className="font-semibold text-base mb-1" style={{ color: 'var(--color-foreground)' }}>
                  Upfront Fees Required at Acceptance
                </p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>The Deal Fee (based on EMD amount, due-diligence timeline, and industry) and the $2,500 Administrative Fee are both due upon acceptance into the EMD Program — before the EMD is funded to the title company. An additional prorated Deal Fee applies only if you request an approved extension of the due diligence period.
                </p>
              </div>
            </div>

            {/* ── Internal link: See Full Pricing ── */}
            <div
              data-reveal
              className="mt-8 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
              style={{
                background: 'var(--color-surface)',
                border: '1px solid rgba(82,183,136,0.2)',
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.4s'
              }}>
              <div>
                <p className="font-semibold text-base mb-1" style={{ color: 'var(--color-foreground)' }}>
                  Want the full pricing breakdown?
                </p>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>One payment - simple fee structure based on EMD size & due diligence.

                </p>
              </div>
              <Link
                href="/pricing"
                className="shrink-0 text-sm font-bold uppercase tracking-[0.15em] px-6 py-3 rounded-[6px] transition-all duration-300 hover:opacity-80"
                style={{
                  background: 'rgba(82,183,136,0.12)',
                  color: 'var(--color-primary)',
                  border: '1px solid rgba(82,183,136,0.3)'
                }}>
                View Pricing →
              </Link>
            </div>
          </div>
        </section>

        {/* ── Key Program Terms ─────────────────────────────────────────── */}
        <section
          className="py-24 px-6"
          style={{ background: 'var(--color-bg)' }}
          aria-labelledby="terms-heading">

          <div className="max-w-7xl mx-auto">
            <div
              data-reveal
              className="text-center mb-16"
              style={{
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>

              <p className="section-label mb-3">Key Terms</p>
              <h2
                id="terms-heading"
                className="font-display"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.05
                }}>

                Program structure —
                <br />
                <span className="italic" style={{ color: 'var(--color-primary)' }}>
                  clearly defined.
                </span>
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {keyTerms.map((term, i) =>
              <div
                key={term.title}
                data-reveal
                className="card-hover rounded-2xl p-8"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid rgba(27,67,50,0.1)',
                  opacity: 0,
                  transform: 'translateY(24px)',
                  filter: 'blur(4px)',
                  transition: `all 0.8s cubic-bezier(0.16,1,0.3,1) ${i * 0.07}s`
                }}>

                  <span className="text-3xl mb-4 block" aria-hidden="true">{term.icon}</span>
                  <h3
                  className="font-display font-semibold text-lg mb-3"
                  style={{ color: 'var(--color-foreground)' }}>

                    {term.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                    {term.body}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Important Notice ──────────────────────────────────────────── */}
        <section
          id="important-notice"
          className="py-16 px-6"
          style={{ background: 'var(--color-bg)' }}
          aria-label="Important program notice">

          <div className="max-w-4xl mx-auto">
            <div
              data-reveal
              className="rounded-3xl p-10 md:p-12"
              style={{
                background: 'var(--color-surface)',
                border: '2px solid rgba(27,67,50,0.15)',
                opacity: 0,
                transform: 'translateY(24px)',
                filter: 'blur(4px)',
                transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)'
              }}>

              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div
                  className="flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                  style={{ background: 'rgba(27,67,50,0.06)' }}>

                  ⚠️
                </div>
                <div>
                  <h3
                    className="font-display font-semibold text-2xl mb-4"
                    style={{ color: 'var(--color-foreground)' }}>

                    Soft Deposits Only — No Hard Deposit Extensions
                  </h3>
                  <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--color-muted)' }}>
                    EarnestBridge&apos;s EMD Program exclusively finances{' '}
                    <strong style={{ color: 'var(--color-foreground)' }}>soft deposits</strong>. Once
                    the earnest money deposit goes hard under the Purchase and Sale Agreement, we will
                    not extend the option period under any circumstances.
                  </p>
                  <p className="text-base leading-relaxed mb-4" style={{ color: 'var(--color-muted)' }}>
                    EarnestBridge is not a lender and the EMD Program is not a debt product. There is no personal guarantee or equity surrendered.
                  </p>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                    If additional due-diligence time is needed, an option-period extension may be requested before the Option Exercise Deadline in conjunction with an approved due-diligence extension. An additional Deal Fee is charged on a prorated basis for the extension period. It is your responsibility to exercise the option or request an extension before the Option Exercise Deadline.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────── */}
        <section
          id="faq"
          className="py-24 px-6 relative z-10"
          style={{ background: 'var(--color-surface)' }}
          aria-labelledby="faq-heading">

          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="section-label mb-3">FAQ</p>
              <h2
                id="faq-heading"
                className="font-display"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  color: 'var(--color-foreground)',
                  letterSpacing: '-0.02em'
                }}>

                Common questions
              </h2>
            </div>

            <div className="divide-y" style={{ borderColor: 'rgba(27,67,50,0.08)' }}>
              {faqs.map((faq, i) =>
              <div key={i} className="py-6">
                  <button
                  className="w-full flex items-start justify-between gap-6 text-left group"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}>

                    <span className="font-semibold text-base" style={{ color: 'var(--color-foreground)' }}>
                      {faq.question}
                    </span>
                    <span
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300"
                    style={{
                      background: openFaq === i ? 'var(--color-primary)' : 'rgba(27,67,50,0.08)',
                      color: openFaq === i ? 'white' : 'var(--color-primary)',
                      transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0deg)'
                    }}>

                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                        d="M7 2v10M2 7h10"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round" />

                      </svg>
                    </span>
                  </button>
                  <div
                  className="overflow-hidden transition-all duration-400"
                  style={{ maxHeight: openFaq === i ? '200px' : '0' }}>

                    <p
                    className="text-sm leading-relaxed pt-4"
                    style={{ color: 'var(--color-muted)' }}>

                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── CTA ───────────────────────────────────────────────────────── */}
        <section
          id="cta"
          className="py-24 px-6 relative z-10"
          style={{ background: 'var(--color-bg)' }}
          aria-label="Call to action">

          <div className="max-w-4xl mx-auto text-center">
            <div
              className="rounded-3xl p-12 md:p-16 relative overflow-hidden"
              style={{ background: 'var(--color-primary)' }}>

              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage: 'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
                  backgroundSize: '24px 24px'
                }} />

              <div className="relative z-10">
                <p className="section-label mb-4" style={{ color: '#74C69D' }}>
                  Ready to Apply?
                </p>
                <h2
                  className="font-display mb-5"
                  style={{
                    fontSize: 'clamp(1.75rem, 4vw, 3rem)',
                    color: 'white',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1
                  }}>

                  Start with a signed LOI.
                  <br />
                  <span className="italic" style={{ color: '#74C69D' }}>
                    We&apos;ll handle the rest.
                  </span>
                </h2>
                <p
                  className="text-base leading-relaxed mb-10 max-w-lg mx-auto"
                  style={{ color: 'rgba(255,255,255,0.6)' }}>

                  Contact EarnestBridge once you have a signed Letter of Intent. Our team will guide
                  you through the application and get your EMD funded quickly.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    href="/contact"
                    className="btn-accent text-sm font-bold uppercase tracking-[0.15em] px-10 py-4 rounded-[6px]">APPLY FOR THE EMD PROGRAM


                  </Link>
                  <Link
                    href="/pricing"
                    className="text-sm font-bold uppercase tracking-[0.15em] px-10 py-4 rounded-[6px] transition-all duration-300 hover:opacity-80"
                    style={{
                      border: '1.5px solid rgba(255,255,255,0.25)',
                      color: 'white'
                    }}>
                    See Pricing
                  </Link>
                </div>
                <p className="mt-6 text-sm" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Or email us at{' '}
                  <a
                    href="mailto:deals@earnestbridge.com"
                    className="font-semibold hover:underline"
                    style={{ color: '#74C69D' }}>

                    deals@earnestbridge.com
                  </a>
                </p>
              </div>
            </div>

            {/* Related reading */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
              <Link
                href="/blog/what-is-earnest-money-deposit-financing"
                className="group rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid rgba(27,67,50,0.1)'
                }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-primary)' }}>
                  Blog Post
                </p>
                <p className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors" style={{ color: 'var(--color-foreground)' }}>
                  What Is EMD Financing? Complete Guide
                </p>
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  Learn the fundamentals before you apply →
                </p>
              </Link>
              <Link
                href="/blog/the-40000-difference-why-equity-splits-are-killing-your-scalability"
                className="group rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid rgba(27,67,50,0.1)'
                }}>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--color-primary)' }}>
                  Blog Post
                </p>
                <p className="font-semibold text-sm mb-1 group-hover:text-primary transition-colors" style={{ color: 'var(--color-foreground)' }}>
                  Why Equity Splits Are Killing Your Scalability
                </p>
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  See the $40,000 difference vs. equity funders →
                </p>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
              }
            }))
          })
        }} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com'
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'EMD Program',
              item: `${process.env.NEXT_PUBLIC_SITE_URL || 'https://earnestbridge.com'}/emd-program`
            }]

          })
        }} />

    </main>);


}